import {
  BadgeCheck,
  Boxes,
  Braces,
  Check,
  Copy,
  Download,
  FileText,
  Search,
  ShieldCheck,
  Sparkles,
  Terminal,
} from "lucide-react";
import * as React from "react";
import conventionsData from "./data/conventions.json";
import soulsData from "./data/souls.json";
import type { Convention, Soul } from "./types";

const souls = soulsData as Soul[];
const conventions = conventionsData as Convention[];

const categories = ["all", ...Array.from(new Set(souls.map((soul) => soul.category)))];
const conventionCategories = [
  "all",
  ...Array.from(new Set(conventions.map((convention) => convention.category))),
];

function installCommand(soul: Soul): string {
  return `npx @dantelabs/hermes-souls install ${soul.id}`;
}

function activeSoulFromUrl(): string | null {
  const params = new URLSearchParams(window.location.search);
  return params.get("soul");
}

function activeConventionFromUrl(): string | null {
  const params = new URLSearchParams(window.location.search);
  return params.get("convention");
}

export function App() {
  const initialSoul = souls.find((soul) => soul.id === activeSoulFromUrl()) ?? souls[0];
  const initialConvention =
    conventions.find((convention) => convention.id === activeConventionFromUrl()) ??
    conventions[0];
  const [mode, setMode] = React.useState<"souls" | "conventions">(
    activeConventionFromUrl() ? "conventions" : "souls",
  );
  const [query, setQuery] = React.useState("");
  const [category, setCategory] = React.useState("all");
  const [conventionCategory, setConventionCategory] = React.useState("all");
  const [selected, setSelected] = React.useState<Soul>(initialSoul);
  const [selectedConvention, setSelectedConvention] =
    React.useState<Convention>(initialConvention);
  const [copied, setCopied] = React.useState<string | null>(null);

  const filteredSouls = souls.filter((soul) => {
    const text = `${soul.id} ${soul.displayName} ${soul.description} ${soul.tags.join(" ")}`;
    const matchesQuery = text.toLowerCase().includes(query.toLowerCase());
    const matchesCategory = category === "all" || soul.category === category;
    return matchesQuery && matchesCategory;
  });

  const filteredConventions = conventions.filter((convention) => {
    const text = `${convention.id} ${convention.displayName} ${convention.description} ${convention.tags.join(" ")}`;
    const matchesQuery = text.toLowerCase().includes(query.toLowerCase());
    const matchesCategory =
      conventionCategory === "all" || convention.category === conventionCategory;
    return matchesQuery && matchesCategory;
  });

  function selectSoul(soul: Soul) {
    setSelected(soul);
    window.history.replaceState(null, "", `?soul=${encodeURIComponent(soul.id)}`);
  }

  function selectConvention(convention: Convention) {
    setSelectedConvention(convention);
    window.history.replaceState(null, "", `?convention=${encodeURIComponent(convention.id)}`);
  }

  function selectMode(nextMode: "souls" | "conventions") {
    setMode(nextMode);
    window.history.replaceState(
      null,
      "",
      nextMode === "souls"
        ? `?soul=${encodeURIComponent(selected.id)}`
        : `?convention=${encodeURIComponent(selectedConvention.id)}`,
    );
  }

  async function copyText(key: string, text: string) {
    await navigator.clipboard.writeText(text);
    setCopied(key);
    window.setTimeout(() => setCopied(null), 1200);
  }

  return (
    <main className="shell">
      <section className="hero">
        <div className="hero__text">
          <div className="eyebrow">
            <Sparkles size={16} />
            Hermes persona registry
          </div>
          <h1>Hermes Souls</h1>
          <p>
            Hermes 에이전트가 즉시 채택할 수 있는 persona, workflow convention,
            instruction 파일을 `SOUL.md` 패키지로 정리한 레지스트리입니다.
          </p>
          <div className="command">
            <Terminal size={18} />
            <code>{installCommand(selected)}</code>
            <button
              aria-label="Copy install command"
              onClick={() => copyText("install", installCommand(selected))}
            >
              {copied === "install" ? <Check size={18} /> : <Copy size={18} />}
            </button>
          </div>
        </div>
        <div className="hero__visual" aria-hidden="true">
          <div className="orbit orbit--one" />
          <div className="orbit orbit--two" />
          <div className="sigil">
            <span>H</span>
            <i />
            <b />
          </div>
          <div className="rune rune--one">SOUL.md</div>
          <div className="rune rune--two">AGENTS.md</div>
          <div className="rune rune--three">STYLE.md</div>
        </div>
      </section>

      <section className="toolbar" aria-label="Soul filters">
        <label className="search">
          <Search size={18} />
          <input
            value={query}
            onChange={(event) => setQuery(event.target.value)}
            placeholder="persona, tool, convention 검색"
          />
        </label>
        <div className="mode-switch" role="tablist" aria-label="Registry mode">
          <button className={mode === "souls" ? "active" : ""} onClick={() => selectMode("souls")}>
            Souls
          </button>
          <button
            className={mode === "conventions" ? "active" : ""}
            onClick={() => selectMode("conventions")}
          >
            Conventions
          </button>
        </div>
        <div className="segments" role="tablist" aria-label="Categories">
          {(mode === "souls" ? categories : conventionCategories).map((item) => (
            <button
              key={item}
              className={
                item === (mode === "souls" ? category : conventionCategory) ? "active" : ""
              }
              onClick={() =>
                mode === "souls" ? setCategory(item) : setConventionCategory(item)
              }
            >
              {item}
            </button>
          ))}
        </div>
      </section>

      <section className="workspace">
        <aside className="registry" aria-label="Registry">
          {mode === "souls"
            ? filteredSouls.map((soul) => (
                <button
                  key={soul.id}
                  className={`soul-card ${selected.id === soul.id ? "selected" : ""}`}
                  onClick={() => selectSoul(soul)}
                >
                  <span className="soul-card__meta">
                    <BadgeCheck size={15} />
                    {soul.risk}
                  </span>
                  <strong>{soul.displayName}</strong>
                  <span>{soul.description}</span>
                  <span className="soul-card__tags">
                    {soul.tags.slice(0, 3).map((tag) => (
                      <i key={tag}>{tag}</i>
                    ))}
                  </span>
                </button>
              ))
            : filteredConventions.map((convention) => (
                <button
                  key={convention.id}
                  className={`soul-card ${
                    selectedConvention.id === convention.id ? "selected" : ""
                  }`}
                  onClick={() => selectConvention(convention)}
                >
                  <span className="soul-card__meta">
                    <FileText size={15} />
                    convention
                  </span>
                  <strong>{convention.displayName}</strong>
                  <span>{convention.description}</span>
                  <span className="soul-card__tags">
                    {convention.tags.slice(0, 3).map((tag) => (
                      <i key={tag}>{tag}</i>
                    ))}
                  </span>
                </button>
              ))}
        </aside>

        {mode === "souls" ? <SoulDetail selected={selected} copied={copied} copyText={copyText} /> : (
          <ConventionDetail
            selected={selectedConvention}
            copied={copied}
            copyText={copyText}
          />
        )}
      </section>
    </main>
  );
}

function SoulDetail({
  selected,
  copied,
  copyText,
}: {
  selected: Soul;
  copied: string | null;
  copyText: (key: string, text: string) => Promise<void>;
}) {
  return (
    <article className="detail">
          <header className="detail__header">
            <div>
              <p>{selected.id}</p>
              <h2>{selected.displayName}</h2>
            </div>
            <div className="score">
              <ShieldCheck size={18} />
              <strong>{selected.score}</strong>
              <span>score</span>
            </div>
          </header>

          <div className="facts">
            <div>
              <FileText size={18} />
              <span>Spec</span>
              <strong>{selected.specVersion}</strong>
            </div>
            <div>
              <Boxes size={18} />
              <span>Files</span>
              <strong>{Object.keys(selected.files).length}</strong>
            </div>
            <div>
              <Download size={18} />
              <span>Installs</span>
              <strong>{selected.downloads}</strong>
            </div>
          </div>

          <section className="panel">
            <div className="panel__title">
              <h3>soul.json</h3>
              <button
                aria-label="Copy soul metadata"
                onClick={() => copyText("json", JSON.stringify(stripBody(selected), null, 2))}
              >
                {copied === "json" ? <Check size={17} /> : <Copy size={17} />}
              </button>
            </div>
            <pre>{JSON.stringify(stripBody(selected), null, 2)}</pre>
          </section>

          <section className="panel">
            <div className="panel__title">
              <h3>SOUL.md</h3>
              <button
                aria-label="Copy SOUL.md"
                onClick={() => copyText("soul", selected.soul)}
              >
                {copied === "soul" ? <Check size={17} /> : <Copy size={17} />}
              </button>
            </div>
            <div className="markdown-preview">
              {selected.soul.split("\n").map((line, index) => renderMarkdownLine(line, index))}
            </div>
          </section>

          <section className="install-panel">
            <Braces size={18} />
            <div>
              <strong>CLI contract</strong>
              <p>
                설치 시 `SOUL.md`, `IDENTITY.md`, `STYLE.md`, `AGENTS.md`,
                `soul.json`이 같은 폴더에 기록됩니다.
              </p>
            </div>
          </section>
        </article>
  );
}

function ConventionDetail({
  selected,
  copied,
  copyText,
}: {
  selected: Convention;
  copied: string | null;
  copyText: (key: string, text: string) => Promise<void>;
}) {
  const install = `npx @dantelabs/hermes-souls install-convention ${selected.id}`;

  return (
    <article className="detail">
      <header className="detail__header">
        <div>
          <p>{selected.id}</p>
          <h2>{selected.displayName}</h2>
        </div>
        <div className="score">
          <FileText size={18} />
          <strong>{selected.version}</strong>
          <span>version</span>
        </div>
      </header>

      <div className="command command--inline">
        <Terminal size={18} />
        <code>{install}</code>
        <button aria-label="Copy convention install command" onClick={() => copyText("conv-install", install)}>
          {copied === "conv-install" ? <Check size={18} /> : <Copy size={18} />}
        </button>
      </div>

      <section className="panel">
        <div className="panel__title">
          <h3>convention.json</h3>
          <button
            aria-label="Copy convention metadata"
            onClick={() =>
              copyText("conv-json", JSON.stringify(stripConventionBody(selected), null, 2))
            }
          >
            {copied === "conv-json" ? <Check size={17} /> : <Copy size={17} />}
          </button>
        </div>
        <pre>{JSON.stringify(stripConventionBody(selected), null, 2)}</pre>
      </section>

      <section className="panel">
        <div className="panel__title">
          <h3>CONVENTION.md</h3>
          <button
            aria-label="Copy CONVENTION.md"
            onClick={() => copyText("convention", selected.convention)}
          >
            {copied === "convention" ? <Check size={17} /> : <Copy size={17} />}
          </button>
        </div>
        <div className="markdown-preview">
          {selected.convention
            .split("\n")
            .map((line, index) => renderMarkdownLine(line, index))}
        </div>
      </section>

      <section className="install-panel">
        <Braces size={18} />
        <div>
          <strong>Convention contract</strong>
          <p>
            설치 시 `CONVENTION.md`, `AGENTS.md`, `convention.json`이 같은 폴더에
            기록됩니다. `--write-agents`를 명시해야 현재 프로젝트의 `AGENTS.md`를 씁니다.
          </p>
        </div>
      </section>
    </article>
  );
}

function stripBody(soul: Soul) {
  const { soul: _soul, identity: _identity, style: _style, agents: _agents, ...meta } = soul;
  return meta;
}

function stripConventionBody(convention: Convention) {
  const { convention: _convention, agents: _agents, ...meta } = convention;
  return meta;
}

function renderMarkdownLine(line: string, index: number) {
  if (line.startsWith("# ")) return <h4 key={index}>{line.replace("# ", "")}</h4>;
  if (line.startsWith("## ")) return <h5 key={index}>{line.replace("## ", "")}</h5>;
  if (line.trim() === "") return <br key={index} />;
  if (/^\d+\./.test(line)) return <p key={index} className="ordered">{line}</p>;
  if (line.startsWith("- ")) return <p key={index} className="bullet">{line}</p>;
  return <p key={index}>{line}</p>;
}
